import { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useConsent } from "@/context/ConsentContext";

const CATEGORIES = [
  {
    key: "necessary",
    label: "Cookies strictement nécessaires",
    desc: "Indispensables au fonctionnement du site : session, panier et paiement sécurisé (PayPal). Toujours actifs.",
    locked: true,
  },
  {
    key: "analytics",
    label: "Mesure d'audience",
    desc: "Google Analytics, pour comprendre l'usage du site et l'améliorer.",
  },
  {
    key: "social",
    label: "Réseaux sociaux",
    desc: "Affichage des contenus Instagram intégrés et cookies associés.",
  },
];

function Toggle({ checked, disabled, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      data-testid={testid}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ${
        checked ? "bg-amber-deep" : "bg-stone-300"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function Manager({ initial, onSave, onClose }) {
  const [prefs, setPrefs] = useState({ analytics: !!initial.analytics, social: !!initial.social });
  return (
    <div data-testid="cookie-manager-overlay" className="fixed inset-0 z-[60] flex items-end justify-center bg-bark/40 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div data-testid="cookie-manager" className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-cream p-6 shadow-2xl sm:rounded-2xl sm:p-8">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <Cookie size={20} className="text-amber-deep" />
            <h2 className="font-serif text-xl font-semibold text-bark">Gérer mes cookies</h2>
          </div>
          <button data-testid="cookie-manager-close" onClick={onClose} aria-label="Fermer" className="rounded-full p-1 text-bark-soft transition-colors hover:bg-sand hover:text-bark">
            <X size={20} />
          </button>
        </div>
        <p className="mb-5 text-sm leading-relaxed text-bark-soft">
          Choisissez les cookies que vous acceptez. Votre choix est conservé 13 mois et modifiable à tout moment.
        </p>
        <div className="space-y-4">
          {CATEGORIES.map((c) => (
            <div key={c.key} className="flex items-start justify-between gap-4 rounded-xl border border-amber-800/15 bg-white p-4">
              <div>
                <p className="text-sm font-semibold text-bark">{c.label}</p>
                <p className="mt-1 text-xs leading-relaxed text-bark-soft">{c.desc}</p>
              </div>
              <Toggle
                testid={`cookie-toggle-${c.key}`}
                checked={c.locked ? true : prefs[c.key]}
                disabled={c.locked}
                onChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))}
              />
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <button
            data-testid="cookie-manager-save"
            onClick={() => onSave(prefs)}
            className="flex-1 rounded-full bg-amber-deep px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-honey"
          >
            Enregistrer mes choix
          </button>
        </div>
        <p className="mt-4 text-center text-xs text-stone-mute">
          <Link to="/cookies" onClick={onClose} className="underline hover:text-amber-deep">Politique cookies</Link>
        </p>
      </div>
    </div>
  );
}

export default function CookieConsent() {
  const { hasChoice, showManager, openManager, closeManager, acceptAll, rejectAll, savePreferences, categories } = useConsent();

  return (
    <>
      {!hasChoice && !showManager && (
        <div data-testid="cookie-banner" className="fixed inset-x-0 bottom-0 z-50 border-t border-amber-900/15 bg-cream/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-5 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <Cookie size={22} className="mt-0.5 shrink-0 text-amber-deep" />
              <p className="text-sm leading-relaxed text-bark-soft">
                Nous utilisons des cookies pour assurer le bon fonctionnement du site et, avec votre accord, mesurer
                l'audience et afficher des contenus Instagram. En savoir plus dans notre{" "}
                <Link to="/cookies" className="font-medium text-amber-deep underline">politique cookies</Link>.
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
              <button
                data-testid="cookie-customize"
                onClick={openManager}
                className="rounded-full border border-amber-800/25 bg-white px-5 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
              >
                Personnaliser
              </button>
              <button
                data-testid="cookie-reject-all"
                onClick={rejectAll}
                className="rounded-full border border-amber-800/25 bg-white px-5 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
              >
                Tout refuser
              </button>
              <button
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-honey"
              >
                Tout accepter
              </button>
            </div>
          </div>
        </div>
      )}
      {showManager && (
        <Manager initial={categories} onSave={savePreferences} onClose={closeManager} />
      )}
      {hasChoice && !showManager && (
        <button
          data-testid="cookie-reopen"
          onClick={openManager}
          aria-label="Gérer mes cookies"
          title="Gérer mes cookies"
          className="fixed bottom-4 left-4 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-amber-800/20 bg-cream/95 text-amber-deep shadow-lg shadow-amber-900/15 backdrop-blur transition-all duration-200 hover:scale-105 hover:border-amber-deep hover:text-honey"
        >
          <Cookie size={20} />
        </button>
      )}
    </>
  );
}
